import React from 'react';
import styled from '@emotion/styled';
import { Textfit } from 'react-textfit';
import Icon from '~ComponentsImproved/Icon';
import CheckIcon from '~Assets/icons/check.svg';
import { QuantityWrapper } from '../GridButtons/ItemButton/Button';
import Heading from '~ComponentsImproved/Typography/Heading';
import { useCurrency } from '~Hooks';
import useI18n from '~Translations';

const Item = styled.div<{
  selected?: boolean;
}>`
  height: 88px;
  border-radius: 4px;
  padding: 4px;
  box-sizing: border-box;
  margin-bottom: 12px;
  position: relative;
  background-color: var(--colorWhite);

  .text-fit {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-flow: column nowrap;
    word-break: break-word;
    text-align: center;
  }

  strong {
    display: block;
    margin-top: 4px;
    font-size: 14px;
  }
`;

const IconContainer = styled.div<{ active: boolean }>`
  position: absolute;
  width: 24px;
  height: 24px;
  display: flex;
  flex-flow: row wrap;
  justify-content: center;
  align-items: center;
  right: 4px;
  bottom: 4px;
  border-radius: 50%;
  border: 1px solid;
  border-color: ${({ active }) => (active ? 'var(--colorUIGreen)' : 'var(--colorBlueGray300)')};
  background-color: ${({ active }) => (active ? 'var(--colorUIGreen)' : 'var(--colorWhite)')};

  svg {
    display: ${({ active }) => (active ? 'block' : 'none')};
  }
`;

const OutOfStock = styled.div`
  position: absolute;
  top: 0;
  left: 0;

  width: 100%;
  height: calc(100% - 16px);
  padding-bottom: 16px;
  border-radius: 4px;

  display: flex;
  align-items: flex-end;
  justify-content: center;

  background-color: rgba(0, 0, 0, 0.5);
  color: var(--colorWhite);
`;

interface Props {
  title: string;
  active: boolean;
  price: number;
  isSingle: boolean;
  isOutOfStock?: boolean;
  onTouchStart: () => void | undefined;
  onClick: () => void | undefined;
}

const ModificationButton = ({
  title,
  active,
  price,
  isSingle,
  isOutOfStock = false,
  onTouchStart,
  onClick,
}: Props) => {
  const { i18n } = useI18n();

  const currency = useCurrency();

  // Don't do anything if the item is out of stock
  const handleTouchStart = () => {
    if (isOutOfStock) {
      return;
    }

    onTouchStart();
  };

  return (
    <Item onTouchStart={handleTouchStart} onClick={onClick}>
      {isOutOfStock && (
        <OutOfStock>
          <QuantityWrapper>
            <Heading level={6} color={'var(--colorWhite)'}>
              {i18n('POS.OutOfStock')}
            </Heading>
          </QuantityWrapper>
        </OutOfStock>
      )}
      <Textfit className="text-fit" max={16} min={12}>
        {title}

        {price > 0 && <strong>{currency.format(price)}</strong>}
      </Textfit>
      {!isOutOfStock && (!isSingle || active) && (
        <IconContainer active={active}>
          <Icon src={CheckIcon} width="14px" height="14px" />
        </IconContainer>
      )}
    </Item>
  );
};

export default ModificationButton;
